"use strict";
/**
 * Inbox Polling Hook
 *
 * Polls for new unsigned documents at regular intervals.
 * Triggers modal and browser notifications when new documents are detected.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useInboxPolling = void 0;
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const unsigned_user_documents_thunk_1 = require("../reducers/slices/document/thunks/unsigned_user_documents_thunk");
const main_1 = require("../reducers/slices/main");
const browserNotifications_1 = require("../helpers/browserNotifications");
const react_router_dom_1 = require("react-router-dom");
const routes_1 = require("../constants/routes");
const POLLING_INTERVAL = 30000; // 30 seconds
/**
 * Hook to poll for new inbox documents
 */
const useInboxPolling = (options = {}) => {
    const { interval = POLLING_INTERVAL, enabled = true } = options;
    const dispatch = (0, react_redux_1.useDispatch)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { user } = (0, react_redux_1.useSelector)((state) => state.userState);
    const { unsignedUserDocuments } = (0, react_redux_1.useSelector)((state) => state.documentState);
    const [isPolling, setIsPolling] = (0, react_1.useState)(false);
    const lastDocumentIdsRef = (0, react_1.useRef)([]);
    const intervalRef = (0, react_1.useRef)(null);
    /**
     * Poll for new documents
     */
    const poll = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(user === null || user === void 0 ? void 0 : user.email) || isPolling) {
            return;
        }
        setIsPolling(true);
        try {
            // Fetch latest documents
            const result = yield dispatch((0, unsigned_user_documents_thunk_1.unsigned_user_documents_thunk)({ email: user.email }));
            // After successful fetch, unsignedUserDocuments will be updated in Redux
            // We'll detect new documents in the next effect
        }
        catch (error) {
            console.error("Failed to poll inbox:", error);
        }
        finally {
            setIsPolling(false);
        }
    });
    /**
     * Detect new documents and trigger notifications
     */
    (0, react_1.useEffect)(() => {
        if (!unsignedUserDocuments || unsignedUserDocuments.length === 0) {
            lastDocumentIdsRef.current = [];
            return;
        }
        const currentDocumentIds = unsignedUserDocuments.map((doc) => doc._id);
        // On first load, just store the IDs without showing notifications
        if (lastDocumentIdsRef.current.length === 0) {
            lastDocumentIdsRef.current = currentDocumentIds;
            return;
        }
        // Detect new documents
        const newDocumentIds = currentDocumentIds.filter((id) => !lastDocumentIdsRef.current.includes(id));
        if (newDocumentIds.length > 0) {
            // Find the new documents
            const newDocuments = unsignedUserDocuments.filter((doc) => newDocumentIds.includes(doc._id));
            // Show notifications for new documents
            if (newDocuments.length === 1) {
                // Single new document - show detailed modal
                const doc = newDocuments[0];
                const senderName = doc.owner ? `${doc.owner.name} ${doc.owner.surname}`.trim() || doc.owner.email : "Unknown";
                const documentTitle = doc.name || "Untitled Document";
                const navigateToDocument = () => {
                    dispatch((0, main_1.setMessageModal)(null));
                    navigate(`${routes_1.CONSOLE_ROUTE}${routes_1.DOCUMENT_ROUTE}/${doc._id}/link`);
                };
                // Show modal notification
                dispatch((0, main_1.setMessageModal)({
                    title: "New Document Received",
                    messages: [`You have received a new document from ${senderName}:`, `"${documentTitle}"`],
                    buttons: [
                        {
                            label: "View Document",
                            backgroundColor: "bg-primary",
                            textColor: "text-white",
                            onClick: navigateToDocument,
                        },
                        {
                            label: "Later",
                            backgroundColor: "bg-white",
                            textColor: "text-gray-700",
                            borderColor: "border-gray-300",
                            onClick: () => dispatch((0, main_1.setMessageModal)(null)),
                        },
                    ],
                }));
                // Show browser notification (only when app is in background)
                (0, browserNotifications_1.showNewDocumentNotification)(senderName, documentTitle, navigateToDocument);
            }
            else {
                // Multiple new documents - show summary modal
                const documentList = newDocuments
                    .map((doc) => {
                    const senderName = doc.owner ? `${doc.owner.name} ${doc.owner.surname}`.trim() || doc.owner.email : "Unknown";
                    return `• "${doc.name || "Untitled"}" from ${senderName}`;
                })
                    .join("\n");
                dispatch((0, main_1.setMessageModal)({
                    title: "New Documents Received",
                    messages: [`You have received ${newDocuments.length} new documents:`, documentList],
                    buttons: [
                        {
                            label: "View Inbox",
                            backgroundColor: "bg-primary",
                            textColor: "text-white",
                            onClick: () => {
                                dispatch((0, main_1.setMessageModal)(null));
                                navigate(`${routes_1.CONSOLE_ROUTE}/inbox`);
                            },
                        },
                        {
                            label: "Later",
                            backgroundColor: "bg-white",
                            textColor: "text-gray-700",
                            borderColor: "border-gray-300",
                            onClick: () => dispatch((0, main_1.setMessageModal)(null)),
                        },
                    ],
                }));
                // Show browser notification for multiple documents
                (0, browserNotifications_1.showNewDocumentNotification)("Multiple Senders", `${newDocuments.length} new documents`, () => navigate(`${routes_1.CONSOLE_ROUTE}/inbox`));
            }
            // Update last document IDs
            lastDocumentIdsRef.current = currentDocumentIds;
        }
    }, [unsignedUserDocuments, navigate]);
    /**
     * Start polling
     */
    const startPolling = () => {
        if (intervalRef.current) {
            return; // Already polling
        }
        // Initial poll
        poll();
        // Set up interval
        intervalRef.current = setInterval(() => {
            poll();
        }, interval);
    };
    /**
     * Stop polling
     */
    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };
    /**
     * Start/stop polling based on enabled flag and user login status
     */
    (0, react_1.useEffect)(() => {
        if (enabled && (user === null || user === void 0 ? void 0 : user._id)) {
            startPolling();
        }
        else {
            stopPolling();
        }
        // Cleanup on unmount
        return () => {
            stopPolling();
        };
    }, [enabled, user === null || user === void 0 ? void 0 : user._id, interval]);
    return {
        isPolling,
        poll,
        startPolling,
        stopPolling,
    };
};
exports.useInboxPolling = useInboxPolling;
exports.default = exports.useInboxPolling;
